/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-mutating-methods */
import { Fragment, ReactElement, useEffect, useState } from 'react'
import dayjs from 'dayjs'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Pagination from '@mui/material/Pagination'
import Select from '@mui/material/Select'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'

import { DashboardIcon, DownloadIcon } from 'src/v3/core/icons'

import theme from 'src/theme'
import axios, { pluckData } from 'src/utilities/http-client'
import SelectLinkedService from 'src/v3/manage/components/linked-service'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { UserExtra } from 'src/user/user.types'
import { downloadFile } from 'src/v3/utilities/filesystem'
import Loading from 'src/v3/components/loading'
import {
  selectActiveLinkedService,
  fetchUserHours,
  selectUserHoursStatus,
  selectUserHoursByLinkedService,
  fetchUsers,
  selectUsersStatus,
  selectUsersByLinkedService,
  fetchCourses,
  selectCourses,
  selectCoursesStatus,
  fetchCompletedCourses,
  selectCompletedCourses,
  selectCompletedCoursesStatus,
  fetchCredentials,
  selectCredentials,
  selectCredentialsStatus,
  fetchAssignments,
  selectAssignmentsByLinkedService,
  selectAssignmentsStatus,
  fetchCompletedAssignments,
  selectCompletedAssignmentsByLinkedService,
  selectCompletedAssignmentsStatus,
  exportCertificates,
  exportCredentials,
  TimeUnit,
} from '../manage.slice'
import QuizAnalytics from './quiz-analytics'
import ExportTranscripts from './export-transcripts'

type TimeUnitAdditional = TimeUnit | '60' | '90' | '120'
const units: TimeUnit[] = ['allTime', 'year', 'month', 'week']
const unitsAdditional: TimeUnitAdditional[] = [
  'allTime',
  'year',
  '120',
  '90',
  '60',
  'month',
  'week',
]
const timeDisplay: Record<TimeUnitAdditional, string> = {
  allTime: 'All Time',
  year: 'Year',
  120: '120 Days',
  90: '90 Days',
  60: '60 Days',
  month: 'Month',
  week: 'Week',
}

const headerCellStyle = {
  padding: '5px',
  backgroundColor: theme.palette.primary.dark,
}


let TimeSpanSelect = ({
  currentTime,
  setTime,
  timeUnits,
}: {
  currentTime: TimeUnit
  setTime: (value: TimeUnit) => void
  timeUnits: TimeUnit[]
}): ReactElement => (
  <div style={{ margin: '5px 0' }}>
    <Typography
      textAlign="center"
      color="text.secondary"
      sx={{ marginBottom: '2px' }}
    >
      Time Span
    </Typography>
    <Stack
      direction="row"
      spacing={1}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        rowGap: 1,
      }}
    >
      {timeUnits.map((unit) => (
        <Chip
          key={unit}
          color="primary"
          label={timeDisplay[unit]}
          onClick={() => setTime(unit)}
          variant={currentTime === unit ? 'filled' : 'outlined'}
          style={{ cursor: 'pointer' }}
        />
      ))}
    </Stack>
  </div>
)

let UserDropdown = ({
  currentUser,
  setUser,
  users,
}: {
  currentUser: string
  setUser: (value: string) => void
  users: UserExtra[]
}): ReactElement => (
  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
    <FormControl sx={{ minWidth: 200, my: '2px' }}>
      <InputLabel size="small" sx={{ paddingRight: 2 }}>
        User
      </InputLabel>
      <Select
        value={currentUser}
        label="User"
        variant="outlined"
        size="small"
        onChange={(event) => setUser(`${event.target.value}`)}
      >
        <MenuItem key="all" value={''}>
          All Users
        </MenuItem>
        {users.map((u) => (
          <MenuItem key={u.id} value={u.id}>
            {u.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </div>
)

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let [dataView, setDataView] = useState<string>('user')
  let [userHoursTime, setUserHoursTime] = useState<TimeUnit>('allTime')
  let [userHoursUser, setUserHoursUser] = useState<string>('')
  let [completedCoursesTime, setCompletedCoursesTime] =
    useState<TimeUnit>('month')
  let [completedCoursesUser, setCompletedCoursesUser] = useState<string>('')
  let [completedCoursesPage, setCompletedCoursesPage] = useState<number>(1)
  let [expiringCredsTime, setExpiringCredsTime] =
    useState<TimeUnitAdditional>('month')
  let [expiringCredsUser, setExpiringCredsUser] = useState<string>('')
  let [expiringCredsPage, setExpiringCredsPage] = useState<number>(1)
  let [pastDueUser, setPastDueUser] = useState<string>('')
  let [pastDuePage, setPastDuePage] = useState<number>(1)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let userHours = useAppSelector(
    selectUserHoursByLinkedService(activeLinkedService),
  )
  let userHoursStatus = useAppSelector(selectUserHoursStatus)
  let users = useAppSelector(selectUsersByLinkedService(activeLinkedService))
  let usersStatus = useAppSelector(selectUsersStatus)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let completedCourses = useAppSelector(selectCompletedCourses)
  let completedCoursesStatus = useAppSelector(selectCompletedCoursesStatus)
  let credentials = useAppSelector(selectCredentials)
  let credentialsStatus = useAppSelector(selectCredentialsStatus)
  let assignments = useAppSelector(
    selectAssignmentsByLinkedService(activeLinkedService),
  )
  let assignmentsStatus = useAppSelector(selectAssignmentsStatus)
  let completedAssignments = useAppSelector(
    selectCompletedAssignmentsByLinkedService(activeLinkedService),
  )
  let completedAssignmentsStatus = useAppSelector(
    selectCompletedAssignmentsStatus,
  )

  let hours =
    userHours?.[userHoursTime].reduce((sum, entry) => sum + entry.hours, 0) || 0

  let recentlyCompleted = courses
    .reduce(
      (rc, c) => {
        let match = completedCourses.find(
          (cc) =>
            cc.courseID === c.id &&
            dayjs(cc.dateCompleted).isAfter(
              dayjs().subtract(1, completedCoursesTime as any),
            ) &&
            users.some((u) => u.id === cc.userID),
        )
        if (match) {
          rc.push({
            name: c.name,
            user: match.userID,
            completed: match.dateCompleted,
          })
        }
        return rc
      },
      [] as { name: string; user: string; completed: string }[],
    )
    .filter((rc) => !completedCoursesUser || rc.user === completedCoursesUser)
    .sort((a, b) => dayjs(b.completed).diff(a.completed))

  let isTimeUnit = units.includes(expiringCredsTime as TimeUnit)
  let expiringSoonDisplay = isTimeUnit
    ? `this ${expiringCredsTime}`
    : `in the next ${timeDisplay[expiringCredsTime].toLowerCase()}`

  let pastDueAssignments = assignments
    .reduce(
      (pda, a) => {
        if (dayjs().isAfter(dayjs(a.dueDate))) {
          a.users.forEach((u) => {
            if (
              users.some((user) => u === user.id) &&
              !completedAssignments.some(
                (ca) => ca.userID === u && ca.assignmentID === a.id,
              )
            ) {
              pda.push({
                user: u,
                assignment: a.name || a.id,
                dueDate: a.dueDate,
              })
            }
          })
        }
        return pda
      },
      [] as { user: string; assignment: string; dueDate: string }[],
    )
    .filter((pda) => !pastDueUser || pda.user === pastDueUser)
    .sort((a, b) => dayjs(b.dueDate).diff(a.dueDate))

  let filteredCredentials = credentials.filter(
    (c) =>
      users.some((u) => u.id === c.userID) &&
      (!expiringCredsUser || expiringCredsUser === c.userID) &&
      (expiringCredsTime === 'allTime' ||
        (isTimeUnit
          ? dayjs(c.expiration).isBefore(
              dayjs().add(1, expiringCredsTime as any),
            )
          : dayjs(c.expiration).isBefore(
              dayjs().add(parseInt(expiringCredsTime, 10), 'day'),
            ))) &&
      dayjs(c.expiration).isAfter(dayjs()),
  )

  let refreshData = () => {
    dispatch(fetchUsers())
    dispatch(fetchUserHours())
    dispatch(fetchCourses())
    dispatch(fetchCompletedCourses())
    dispatch(fetchCredentials())
    dispatch(fetchAssignments())
    dispatch(fetchCompletedAssignments())
  }

  useEffect(() => {
    if (userHoursStatus === 'uninitialized') {
      dispatch(fetchUserHours())
    }
  }, [userHoursStatus])

  useEffect(() => {
    if (usersStatus === 'uninitialized') {
      dispatch(fetchUsers())
    }
  }, [usersStatus])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  useEffect(() => {
    if (completedCoursesStatus === 'uninitialized') {
      dispatch(fetchCompletedCourses())
    }
  }, [completedCoursesStatus])

  useEffect(() => {
    if (credentialsStatus === 'uninitialized') {
      dispatch(fetchCredentials())
    }
  }, [credentialsStatus])

  useEffect(() => {
    if (assignmentsStatus === 'uninitialized') {
      dispatch(fetchAssignments())
    }
  }, [assignmentsStatus])

  useEffect(() => {
    if (completedAssignmentsStatus === 'uninitialized') {
      dispatch(fetchCompletedAssignments())
    }
  }, [completedAssignmentsStatus])

  useEffect(() => {
    setUserHoursUser('')
    setCompletedCoursesUser('')
    setExpiringCredsUser('')
    setPastDueUser('')
  }, [activeLinkedService])

  return (
    <>
      <div className="flex items-center justify-center my-2">
        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
          <DashboardIcon />
        </Avatar>
        <Typography
          variant="h4"
          className="pl-2"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          Manager Dashboard
        </Typography>
      </div>
      <Typography paragraph textAlign="center" color="text.secondary">
        View quick user stats & recent events
      </Typography>
      <div className="flex justify-center">
        <SelectLinkedService linkedServiceID={activeLinkedService} />
      </div>
      <div className="flex justify-center">
        <Tabs
          sx={{ mb: 2 }}
          variant="standard"
          value={dataView}
          onChange={(e, num) => setDataView(num)}
        >
          <Tab value="user" label="User" />
          <Tab value="quiz" label="Quiz" />
        </Tabs>
      </div>
      {dataView === 'user' ? (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexGrow: 1,
            }}
          >
            <Button variant="contained" sx={{ mb: 2 }} onClick={refreshData}>
              Refresh to view latest data
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              columnGap: 2,
              mt: -1,
              mb: 1,
            }}
          >
            <Box
              sx={{
                my: -1,
                flexBasis: 300,
                flexGrow: 1,
                maxWidth: 400,
              }}
            >
              <Card
                variant="outlined"
                sx={{ my: 2, p: 2, minWidth: 'fit-content' }}
              >
                <Typography variant="h6" fontFamily="revert" textAlign="center">
                  User Hours
                </Typography>
                <TimeSpanSelect
                  timeUnits={units}
                  currentTime={userHoursTime}
                  setTime={(unit: TimeUnit) => {
                    setUserHoursTime(unit)
                    if (
                      !userHours?.[unit].find(
                        (uh) => uh.userID === userHoursUser,
                      )
                    ) {
                      setUserHoursUser('')
                    }
                  }}
                />
                <div style={{ margin: '15px 0' }}>
                  <Typography
                    textAlign="center"
                    color="text.secondary"
                    sx={{ marginBottom: '2px' }}
                  >
                    Hours by Linked Service
                  </Typography>
                  <Typography textAlign="center">
                    All users have combined for {hours}{' '}
                    {hours === 1 ? 'hour' : 'hours'}{' '}
                    {userHoursTime === 'allTime'
                      ? 'all time'
                      : `this ${userHoursTime}`}
                    .
                  </Typography>
                </div>
                <Typography
                  textAlign="center"
                  color="text.secondary"
                  sx={{ marginBottom: '-10px' }}
                >
                  Hours by User
                </Typography>
                {!userHours?.[userHoursTime].length ? (
                  <Typography sx={{ mt: '10px' }} textAlign="center">
                    No users have completed any hours{' '}
                    {userHoursTime === 'allTime'
                      ? 'all time'
                      : `this ${userHoursTime}`}
                    .
                  </Typography>
                ) : (
                  <UserDropdown
                    users={users.filter((u) =>
                      userHours?.[userHoursTime].some(
                        (uh) => uh.userID === u.id,
                      ),
                    )}
                    currentUser={userHoursUser}
                    setUser={setUserHoursUser}
                  />
                )}
                {userHoursUser && (
                  <Typography sx={{ mt: 1 }} textAlign="center">
                    {users.find((u) => u.id === userHoursUser)?.name} has
                    completed{' '}
                    {userHours?.[userHoursTime].find(
                      (uh) => uh.userID === userHoursUser,
                    )?.hours || 0}{' '}
                    {userHours?.[userHoursTime].find(
                      (uh) => uh.userID === userHoursUser,
                    )?.hours === 1
                      ? 'hour'
                      : 'hours'}{' '}
                    {userHoursTime === 'allTime'
                      ? 'all time'
                      : `this ${userHoursTime}`}
                    .
                  </Typography>
                )}
              </Card>
              <Card
                variant="outlined"
                sx={{ my: 2, p: 2, minWidth: 'fit-content' }}
              >
                <Typography variant="h6" fontFamily="revert" textAlign="center">
                  All Certificates
                </Typography>
                <div className="flex justify-center mt-2">
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    disabled={activeLinkedService === 'unselected'}
                    onClick={() =>
                      dispatch(exportCertificates(activeLinkedService))
                        .catch((err) =>
                          setErrorMessage(
                            `CSV Export Failed: ${err.response?.data || err}`,
                          ),
                        )
                    }
                  >
                    Download CSV
                  </Button>
                </div>
              </Card>
              <Card
                variant="outlined"
                sx={{ my: 2, p: 2, minWidth: 'fit-content' }}
              >
                <Typography variant="h6" fontFamily="revert" textAlign="center">
                  All Credentials
                </Typography>
                <div className="flex justify-center mt-2">
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    disabled={activeLinkedService === 'unselected'}
                    onClick={() =>
                      dispatch(exportCredentials(activeLinkedService))
                        .catch((err) =>
                          setErrorMessage(
                            `CSV Export Failed: ${err.response?.data || err}`,
                          ),
                        )
                    }
                  >
                    Download CSV
                  </Button>
                </div>
              </Card>
              <Card
                variant="outlined"
                sx={{ my: 2, p: 2, minWidth: 'fit-content' }}
              >
                <Typography variant="h6" fontFamily="revert" textAlign="center">
                  Credentials Expiring Soon
                </Typography>
                <TimeSpanSelect
                  currentTime={expiringCredsTime as TimeUnit}
                  setTime={setExpiringCredsTime}
                  timeUnits={unitsAdditional.slice(2) as TimeUnit[]}
                />
                <UserDropdown
                  currentUser={expiringCredsUser}
                  setUser={setExpiringCredsUser}
                  users={users}
                />
                {filteredCredentials.length ? (
                  <div
                    style={{
                      marginTop: '10px',
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        maxHeight: 300,
                        overflow: 'scroll',
                        display: 'grid',
                        gridTemplateColumns: !expiringCredsUser
                          ? '1fr 1fr 90px'
                          : '1fr 90px',
                        gridTemplateRows: 'auto',
                      }}
                    >
                      {!expiringCredsUser && (
                        <div style={headerCellStyle}>User</div>
                      )}
                      <div style={headerCellStyle}>Credential</div>
                      <div style={headerCellStyle}>Expiration</div>
                      {filteredCredentials
                        .slice(
                          10 * (expiringCredsPage - 1),
                          10 * expiringCredsPage,
                        )
                        .map((c, i) => {
                          let rowCellStyle = {
                            padding: headerCellStyle.padding,
                            backgroundColor:
                              i % 2 ? theme.palette.divider : undefined,
                          }
                          return (
                            <Fragment key={c.id}>
                              {!expiringCredsUser && (
                                <div style={rowCellStyle}>
                                  {users.find((u) => u.id === c.userID)?.name}
                                </div>
                              )}
                              <div style={rowCellStyle}>{c.credentialType}</div>
                              <div style={rowCellStyle}>
                                {dayjs(c.expiration).format('MM/DD/YYYY')}
                              </div>
                            </Fragment>
                          )
                        })}
                    </Box>
                    <Divider />
                    <Pagination
                      size="small"
                      sx={{
                        paddingY: '5px',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                      count={Math.ceil(filteredCredentials.length / 10)}
                      page={expiringCredsPage}
                      onChange={(
                        event: React.ChangeEvent<unknown>,
                        value: number,
                      ) => {
                        setExpiringCredsPage(value)
                      }}
                      showFirstButton
                      showLastButton
                    />
                  </div>
                ) : (
                  <Typography sx={{ mt: 1 }} textAlign="center">
                    {!expiringCredsUser
                      ? 'No users have any'
                      : `${users.find((u) => u.id === expiringCredsUser)?.name} has no`}{' '}
                    expiring credentials {expiringSoonDisplay}.
                  </Typography>
                )}
              </Card>
            </Box>
            <Box
              sx={{
                my: -1,
                flexBasis: 300,
                flexGrow: 1,
                maxWidth: 400,
              }}
            >
              <Card
                variant="outlined"
                sx={{ my: 2, p: 2, minWidth: 'fit-content' }}
              >
                <Typography variant="h6" fontFamily="revert" textAlign="center">
                  Recently Completed Assignments
                </Typography>
                <TimeSpanSelect
                  currentTime={completedCoursesTime}
                  setTime={setCompletedCoursesTime}
                  timeUnits={units.slice(2)}
                />
                <UserDropdown
                  users={users}
                  currentUser={completedCoursesUser}
                  setUser={setCompletedCoursesUser}
                />
                {recentlyCompleted.length ? (
                  <div
                    style={{
                      marginTop: '10px',
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        maxHeight: 300,
                        overflow: 'scroll',
                        display: 'grid',
                        gridTemplateColumns: !completedCoursesUser
                          ? '1fr 1fr 90px'
                          : '1fr 90px',
                        gridTemplateRows: 'auto',
                      }}
                    >
                      {!completedCoursesUser && (
                        <div style={headerCellStyle}>User</div>
                      )}
                      <div style={headerCellStyle}>Course Name</div>
                      <div style={headerCellStyle}>Completed</div>
                      {recentlyCompleted
                        .slice(
                          10 * (completedCoursesPage - 1),
                          10 * completedCoursesPage,
                        )
                        .map((rc, i) => {
                          let rowCellStyle = {
                            padding: headerCellStyle.padding,
                            backgroundColor:
                              i % 2 ? theme.palette.divider : undefined,
                          }
                          return (
                            <Fragment key={rc.name + rc.user + i}>
                              {!completedCoursesUser && (
                                <div style={rowCellStyle}>
                                  {users.find((u) => u.id === rc.user)?.name}
                                </div>
                              )}
                              <div style={rowCellStyle}>{rc.name}</div>
                              <div style={rowCellStyle}>
                                {dayjs(rc.completed).format('MM/DD/YYYY')}
                              </div>
                            </Fragment>
                          )
                        })}
                    </Box>
                    <Divider />
                    <Pagination
                      size="small"
                      sx={{
                        paddingY: '5px',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                      count={Math.ceil(recentlyCompleted.length / 10)}
                      page={completedCoursesPage}
                      onChange={(
                        event: React.ChangeEvent<unknown>,
                        value: number,
                      ) => {
                        setCompletedCoursesPage(value)
                      }}
                      showFirstButton
                      showLastButton
                    />
                  </div>
                ) : (
                  <Typography sx={{ mt: 1 }} textAlign="center">
                    {!completedCoursesUser
                      ? 'No users have'
                      : `${users.find((u) => u.id === completedCoursesUser)?.name} has not`}{' '}
                    completed any courses this {completedCoursesTime}.
                  </Typography>
                )}
              </Card>
              <Card
                variant="outlined"
                sx={{ my: 2, p: 2, minWidth: 'fit-content' }}
              >
                <Typography variant="h6" fontFamily="revert" textAlign="center">
                  Past Due Assignments
                </Typography>
                <UserDropdown
                  users={users}
                  currentUser={pastDueUser}
                  setUser={setPastDueUser}
                />
                {pastDueAssignments.length ? (
                  <div
                    style={{
                      marginTop: '10px',
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        maxHeight: 300,
                        overflow: 'scroll',
                        display: 'grid',
                        gridTemplateColumns: !pastDueUser
                          ? '1fr 1fr 90px'
                          : '1fr 90px',
                        gridTemplateRows: 'auto',
                      }}
                    >
                      {!pastDueUser && <div style={headerCellStyle}>User</div>}
                      <div style={headerCellStyle}>Assignment</div>
                      <div style={headerCellStyle}>Due Date</div>
                      {pastDueAssignments
                        .slice(10 * (pastDuePage - 1), 10 * pastDuePage)
                        .map((pda, i) => {
                          let rowCellStyle = {
                            padding: headerCellStyle.padding,
                            backgroundColor:
                              i % 2 ? theme.palette.divider : undefined,
                          }
                          return (
                            <Fragment key={pda.assignment + pda.user + i}>
                              {!pastDueUser && (
                                <div style={rowCellStyle}>
                                  {users.find((u) => u.id === pda.user)?.name}
                                </div>
                              )}
                              <div style={rowCellStyle}>{pda.assignment}</div>
                              <div style={rowCellStyle}>
                                {dayjs(pda.dueDate).format('MM/DD/YYYY')}
                              </div>
                            </Fragment>
                          )
                        })}
                    </Box>
                    <Divider />
                    <Pagination
                      size="small"
                      sx={{
                        paddingY: '5px',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                      count={Math.ceil(pastDueAssignments.length / 10)}
                      page={pastDuePage}
                      onChange={(
                        event: React.ChangeEvent<unknown>,
                        value: number,
                      ) => {
                        setPastDuePage(value)
                      }}
                      showFirstButton
                      showLastButton
                    />
                  </div>
                ) : (
                  <Typography sx={{ mt: 1 }} textAlign="center">
                    {!completedCoursesUser
                      ? 'No users have any'
                      : `${users.find((u) => u.id === pastDueUser)?.name} has no`}{' '}
                    past due assignments.
                  </Typography>
                )}
              </Card>
              <ExportTranscripts />
            </Box>
          </Box>
        </>
      ) : (
        <QuizAnalytics />
      )}
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading
        entities={[
          assignmentsStatus,
          completedAssignmentsStatus,
          completedCoursesStatus,
          coursesStatus,
          credentialsStatus,
          userHoursStatus,
          usersStatus,
        ]}
      />
    </>
  )
}
